import React, { useCallback, useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import { Menu } from '@ui/menus/Menu';
import { Icon, isQuantityIconName } from '@ui/Icon/Icon';
import { IElement } from '@shared/models/project.model';
import {
  elementQuantityUnitRecord,
  ElementQuantityExpressionName,
  ElementQuantityName,
} from '@shared/models/element-quantities.model';
import {
  getQuantityDisplayUnitForEdit,
  getSelectedElementQuantityProperty,
  isElementQuantityName,
  isElementQuantityProperty,
} from '@shared/helpers/element-quantity.helpers';
import { buildElementListRowQuantitySaveChanges } from '@shared/helpers/actions/element-list-row-quantity-save.helpers';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { getFallbackName } from '@shared/helpers/project.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';
import { selectableUnitHarmonizer } from '@shared/helpers/unit.helpers';
import { labelFromValue } from '@shared/helpers/string.helpers';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { useExpressionDisplayValues } from '@features/expressions/ExpressionInput/use-expression-display-values';
import { useSetNameOfElements } from '@client/hooks/use-element-name.hooks';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { useDataTables } from '@features/data-table/store/data-table-store.hooks';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis/pvgis.hook';
import { useResolvedElement } from '@client/hooks/use-resolved-version.hooks';
import { useSelectedVersion } from '@client/store/ui';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import { useIconQuantityProperties } from '@client/hooks/use-quantity-properties.hooks';
import { useQuantityCountPanel } from '@client/hooks/use-quantity-count-panel.hook';
import { useElementExpressionVariablesById } from '@client/hooks/use-element-expression-variables.hooks';
import { useRecipe } from '@features/recipes/use-recipes.hooks';
import { getProject } from '@client/store/project/project.hook';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { getDecimalDisplayNumber } from '@shared/helpers/display/get-decimal-display-number.helper';
import { getElementListRowDisplay } from './element-list-row-display.helpers';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_UNIT_UPDATE } from '@shared/constants';
import type { IconProps } from '@ui/Icon';

export type ElementRowEditing = {
  /** Element resolved against the selected proposal (or baseline when none). */
  displayElement: IElement;
  name: string;
  namePlaceholder: string | undefined;
  categoryIcon: IconProps<'elementCategory'>['icon'] | undefined;
  quantityValue: string | undefined;
  quantityDisplayValue: string | undefined;
  quantityDisplayNumber: number | undefined;
  quantityDisplayUnit: string;
  quantityPlaceholder: string | undefined;
  /** Editable number filled on ArrowRight when the quantity field is empty. */
  quantityFillValue: string | undefined;
  unit: string;
  handleNameSave: (value: string) => void;
  handleQtySave: (value: number | undefined) => Promise<void>;
  /** Undefined when no alternative units are selectable. */
  onUnitClick: React.MouseEventHandler<HTMLElement> | undefined;
  /** Undefined when the element is readonly. */
  onQtyFxClick: (() => void) | undefined;
  /** Render this once next to the row so the unit popover anchors correctly. */
  unitMenu: React.ReactNode;
  readonly: boolean;
  isDeactivated: boolean;
};

/**
 * Editing surface shared by ElementListRowConnected (Elements tab) and the
 * csv-import row when it represents an already-materialized element. Owns
 * name / quantity / unit save handlers and the unit popover. Display values
 * use the same display contract as ExpressionInput so what the row shows
 * matches the right side panel.
 */
export const useElementRowEditing = (element: IElement): ElementRowEditing => {
  const resolvedForList = useResolvedElement(element.id);
  const displayElement = isElement(resolvedForList) ? resolvedForList : element;

  const baselineVersion = useSelectedVersion();
  const selectedProposalId = useSelectedProposalId();
  const setNameOfElements = useSetNameOfElements();
  const applyChanges = useApplyProjectChanges();
  const environmentHourData = usePvgisEnvironmentHourData();
  const dataTables = useDataTables();
  const readonly = useIsReadonlyElement(element);
  const iconQuantities = useIconQuantityProperties(displayElement);

  const [unitMenuAnchor, setUnitMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const selectedQuantityProperty =
    getSelectedElementQuantityProperty(displayElement);
  const categoryQuantityDisplayUnit =
    getQuantityDisplayUnitForEdit(displayElement);
  const selectedQuantity = selectedQuantityProperty?.name ?? 'area_top';
  const qtyProp =
    displayElement.quantity?.[
      selectedQuantity as ElementQuantityExpressionName
    ];
  const unit = elementQuantityUnitRecord[selectedQuantity] ?? '';

  const quantityVariables = useElementExpressionVariablesById(
    displayElement.id,
    selectedQuantityProperty?.name,
    selectedQuantityProperty?.category_id,
  );

  const quantitySolveOptions = useMemo(
    () => ({ min: qtyProp?.min, max: qtyProp?.max }),
    [qtyProp?.min, qtyProp?.max],
  );

  const expressionDisplay = useExpressionDisplayValues({
    isExpressionPanelMine: false,
    isFocused: false,
    originalValue: qtyProp?.count,
    localValue: qtyProp?.count,
    fallbackValue: qtyProp?.fallbackCount,
    localUnit: qtyProp ? selectableUnitHarmonizer(qtyProp.unit) : undefined,
    displayUnit: categoryQuantityDisplayUnit,
    variables: quantityVariables,
    solveOptions: quantitySolveOptions,
  });

  const quantityValue =
    qtyProp && expressionDisplay.value != null
      ? getDecimalDisplayNumber(expressionDisplay.value)
      : undefined;

  // Editable number the empty quantity field fills on ArrowRight: the raw
  // placeholder value formatted like a committed value (never the pretty
  // "7.85 × 10¹²" placeholder string).
  const quantityFillValue =
    qtyProp && expressionDisplay.placeholderNumber != null
      ? getDecimalDisplayNumber(expressionDisplay.placeholderNumber)
      : undefined;

  const display = useMemo(
    () =>
      qtyProp
        ? getElementListRowDisplay({
            typedValue: expressionDisplay.value,
            typedUnit: qtyProp.unit,
            customDisplayUnit: categoryQuantityDisplayUnit,
            defaultUnit: unit,
            fallbackUnit: expressionDisplay.unit || undefined,
            fallbackPlaceholder: expressionDisplay.placeholder,
            fallbackDisplayNumber: expressionDisplay.placeholderNumber,
          })
        : undefined,
    [
      qtyProp,
      expressionDisplay.value,
      expressionDisplay.unit,
      expressionDisplay.placeholder,
      expressionDisplay.placeholderNumber,
      categoryQuantityDisplayUnit,
      unit,
    ],
  );

  const quantityDisplayValue = display?.displayValue;
  const quantityDisplayNumber = display?.displayNumber;
  const quantityDisplayUnit = display?.displayUnit ?? unit;
  const quantityPlaceholder = display?.placeholder;

  const categoryIcon = displayElement.category_id
    ? getElementIconCategoryName(displayElement.category_id)
    : undefined;

  const recipe = useRecipe(displayElement);
  const category = getElementCategory(displayElement);
  const namePlaceholder =
    recipe?.name ?? getFallbackName(displayElement) ?? category?.name;

  // Data only — the per-option <Icon> is built in `unitMenu` when the menu is
  // open, so a row that never opens the unit popover pays no element-creation.
  const selectableQuantityOptions = useMemo(() => {
    if (
      !iconQuantities.length ||
      category?.disableQuantitySelection ||
      !isElementQuantityProperty(selectedQuantityProperty)
    ) {
      return [];
    }
    const options = iconQuantities.map(({ name }) => ({
      value: name,
      selected: [
        selectedQuantityProperty.name,
        displayElement.selectedQuantity,
      ].includes(name),
    }));
    return sortBy(options, 'value');
  }, [
    iconQuantities,
    category,
    selectedQuantityProperty,
    displayElement.selectedQuantity,
  ]);

  const handleNameSave = useCallback(
    (value: string) => {
      setNameOfElements(value || undefined, element);
    },
    [element, setNameOfElements],
  );

  const handleQtySave = useCallback(
    async (value: number | undefined) => {
      const selectedQuantityName =
        selectedQuantityProperty?.name ??
        (displayElement.selectedQuantity as
          ElementQuantityExpressionName | undefined);

      const { changes, proposalAppend } =
        buildElementListRowQuantitySaveChanges({
          displayElement,
          elementId: element.id,
          value,
          selectedProposalId,
          baselineVersion,
          selectedQuantityName,
          enrichContext: selectedProposalId
            ? {
                project: getProject(),
                weatherHours: environmentHourData ?? getEmptyFrozenArray(),
                dataTables,
              }
            : undefined,
        });

      if (changes.length === 0) return;
      await applyChanges(changes, { proposalAppend });
    },
    [
      applyChanges,
      baselineVersion,
      dataTables,
      displayElement,
      element.id,
      environmentHourData,
      selectedProposalId,
      selectedQuantityProperty,
    ],
  );

  const handleUnitClick = useCallback<React.MouseEventHandler<HTMLElement>>(
    (e) => {
      if (!selectableQuantityOptions.length) return;
      e.stopPropagation();
      setUnitMenuAnchor(e.currentTarget);
    },
    [selectableQuantityOptions.length],
  );

  const handleQuantityChange = useCallback(
    async (name: ElementQuantityName) => {
      if (!isElementQuantityName(name)) return;
      await applyChanges([
        {
          type: ProjectChangeType.ElementQuantitySelect,
          elementId: element.id,
          quantityName: name,
        },
      ]);
      amplitudeLog(AMP_ELEMENT_UNIT_UPDATE, {
        ElementID: element.id,
        unit: name,
      });
      setUnitMenuAnchor(null);
    },
    [applyChanges, element.id],
  );

  const onQtyFxClick = useQuantityCountPanel(
    displayElement,
    selectedQuantity as ElementQuantityExpressionName,
    readonly,
  );

  const unitMenuOpen = unitMenuAnchor !== null;
  const unitMenu = (
    <Menu
      open={unitMenuOpen}
      anchorEl={unitMenuAnchor}
      onClose={() => setUnitMenuAnchor(null)}
    >
      {unitMenuOpen &&
        selectableQuantityOptions.map(({ value, selected }) => (
          <Menu.Item
            key={value}
            selected={selected}
            icon={
              isQuantityIconName(value) ? (
                <Icon category="quantity" icon={value} />
              ) : null
            }
            onClick={() => void handleQuantityChange(value)}
          >
            {labelFromValue(value, true)}
          </Menu.Item>
        ))}
    </Menu>
  );

  return {
    displayElement,
    name: displayElement.name,
    namePlaceholder,
    categoryIcon,
    quantityValue,
    quantityDisplayValue,
    quantityDisplayNumber,
    quantityDisplayUnit,
    quantityPlaceholder,
    quantityFillValue,
    unit,
    handleNameSave,
    handleQtySave,
    onUnitClick: selectableQuantityOptions.length ? handleUnitClick : undefined,
    onQtyFxClick: readonly ? undefined : onQtyFxClick,
    unitMenu,
    readonly,
    isDeactivated: Boolean(displayElement.isDeactivated),
  };
};
